import '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { Menu } from '@zorro/design-system/v2/Menu';
import { ModalMessage } from '@zorro/design-system/v2/ModalMessage';
import { ThreeDotsIcon } from '@zorro/shared/assets';
import { formatDateISO, getNow } from '@zorro/shared/formatters';
import {
  exportToCsv,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

import { useAnalytics } from '../Analytics';
import { ActionsSubMenu } from '../EmployeeActions';
import { FinchEligibilityForm } from './FinchEligibilityForm';
import { FinchSyncSettingsForm } from './FinchSyncSettingsForm';
import { flattenForCsv, normalizeRowsForCsv } from './finchCsvUtils';
import { useFinchConnection } from './useFinchConnection';

type Props = {
  finchProviderDisplayName: string;
  employerId: string;
  finchEmployerId: string;
};

export const FinchConnectionCardDotsMenu = ({
  finchProviderDisplayName,
  employerId,
  finchEmployerId,
}: Props) => {
  const idempotencyKey = useIdempotencyKey();
  const { isZorroOperations, isEmployerAdmin } = useRoles();
  const [open, setOpen] = useState<boolean>(false);
  const [isSetupOpen, setIsSetupOpen] = useState<boolean>(false);

  const [isDisconnectPayrollModalOpen, setIsDisconnectPayrollModalOpen] =
    useState<boolean>(false);

  const [isEligibilityOpen, setIsEligibilityOpen] = useState<boolean>(false);
  const [isEligibilityDirty, setIsEligibilityDirty] = useState<boolean>(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState<boolean>(false);
  const { sendEvent } = useAnalytics();

  const { tryMutate: syncCompany } = useMonolithMutation({
    method: 'finchControllerSyncCompany',
  });
  const { tryMutate: syncEnrollments } = useMonolithMutation({
    method: 'finchControllerSyncEnrollments',
  });
  const { tryMutate: syncProviders } = useMonolithMutation({
    method: 'finchControllerSyncProviders',
  });
  const { tryMutate: introspect } = useMonolithMutation({
    method: 'finchControllerIntrospect',
  });
  const { tryMutate: disconnect } = useMonolithMutation({
    successMessage: 'HR system sync disconnected successfully.',
    method: 'finchControllerDisconnect',
  });
  const { tryMutate: syncEmploymentCandidates } = useMonolithMutation({
    method: 'finchControllerSyncEmploymentCandidates',
  });
  const { tryMutate: syncLinkedEmployees } = useMonolithMutation({
    method: 'finchControllerSyncLinkedEmployees',
  });
  const { tryMutate: exportEmploymentData } = useMonolithMutation({
    method: 'finchControllerExportEmploymentData',
  });

  const { handleConnect: handleReauthenticate } = useFinchConnection({
    employerId,
    successMessage: 'Reauthenticate successfully.',
  });

  const {
    data: { classDefinitions } = { classDefinitions: [] },
    isFetching: isFetchingClassDefinitions,
  } = useMonolithQuery({
    method: 'allowanceModelControllerGetAllClassDefinitionsForEmployer',
    params: employerId && [employerId],
  });

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: [employerId],
  });

  const handleExportEmploymentData = async () => {
    const result = await exportEmploymentData([employerId]);
    if (!result.isOk()) return;
    const flattened = result.value.items.map((item) =>
      flattenForCsv(item as Record<string, unknown>)
    );
    const rows = normalizeRowsForCsv(flattened);
    const employerSlug = (employer?.name ?? employerId)
      .replace(/\s+/gu, '-')
      .toLowerCase();
    exportToCsv(
      rows,
      `finch-employment-${employerSlug}-${formatDateISO(getNow())}.csv`
    );
  };

  const isEmploymentSyncOn = employer?.isEmploymentSyncOn ?? false;
  const isPayrollSyncOn = employer?.isPayrollSyncOn ?? false;

  const syncActionsMenuItems = [
    {
      label: 'Payroll: company sync',
      action: () => syncCompany([employerId]),
      show: isZorroOperations && isPayrollSyncOn,
    },
    {
      label: 'Payroll: enrollments sync',
      action: () => syncEnrollments([employerId]),
      show: isZorroOperations && isPayrollSyncOn,
    },
    {
      label: 'Employment: new employees sync',
      action: () => syncEmploymentCandidates([employerId]),
      show: isZorroOperations && isEmploymentSyncOn,
    },
    {
      label: 'Employment: existing employees sync',
      action: () => syncLinkedEmployees([employerId, idempotencyKey.get()]),
      show: isZorroOperations && isEmploymentSyncOn,
    },
    {
      label: 'Employment: fetch and export (CSV)',
      action: handleExportEmploymentData,
      show: isZorroOperations && isEmploymentSyncOn,
    },
    {
      label: 'Sync providers',
      action: () => syncProviders([]),
      show: isZorroOperations,
    },
    {
      label: 'Update status',
      action: () => introspect([{ employerId }]),
      show: isZorroOperations,
    },
  ];
  const settingsMenuItems = [
    {
      label: 'Payroll sync settings',
      action: () => setIsSetupOpen(true),
      show: isZorroOperations && isPayrollSyncOn,
    },
    {
      label: 'Employment configuration',
      action: () => setIsEligibilityOpen(true),
      show: isZorroOperations && isEmploymentSyncOn,
    },
  ];
  const reauthenticateMenuItem = {
    label: 'Reauthenticate',
    action: () => handleReauthenticate(),
    show: isEmployerAdmin || isZorroOperations,
  };
  const disconnectMenuItem = {
    label: 'Disconnect',
    action: () => setIsDisconnectPayrollModalOpen(true),
    show: isZorroOperations,
  };

  const closeAndResetEligibility = () => {
    void sendEvent('finch_eligibility_form_close_and_reset', {
      employerId,
    });
    setIsEligibilityOpen(false);
    setIsEligibilityDirty(false);
  };

  const handleCloseEligibilityDrawerAndPopUpModal = () => {
    if (isEligibilityDirty) {
      setIsDiscardModalOpen(true);
      void sendEvent('open_eligibility_form_discard_changes_modal', {
        employerId,
      });
    } else {
      closeAndResetEligibility();
    }
  };

  const handleDiscardChanges = () => {
    void sendEvent('discard_eligibility_form_changes', {
      employerId,
    });
    setIsDiscardModalOpen(false);
    closeAndResetEligibility();
  };

  return (
    <>
      <Menu opened={open} onChange={setOpen} size="sm">
        <Menu.Target>
          <ActionIcon isChecked={open}>
            <ThreeDotsIcon />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <ActionsSubMenu title="Sync actions">
            {syncActionsMenuItems
              .filter((item) => item.show)
              .map((item) => {
                return (
                  <Menu.Item
                    key={item.label}
                    onClick={async () => {
                      await item.action();
                      void sendEvent('hr_system_sync_menu_click', {
                        employerId,
                        action_category: 'sync_actions',
                        action_name: item.label,
                      });
                    }}
                  >
                    {item.label}
                  </Menu.Item>
                );
              })}
          </ActionsSubMenu>
          <ActionsSubMenu title="Settings">
            {settingsMenuItems
              .filter((item) => item.show)
              .map((item) => {
                return (
                  <Menu.Item
                    key={item.label}
                    onClick={() => {
                      item.action();
                      void sendEvent('hr_system_sync_menu_click', {
                        employerId,
                        action_category: 'settings',
                        action_name: item.label,
                      });
                    }}
                  >
                    {item.label}
                  </Menu.Item>
                );
              })}
          </ActionsSubMenu>
          {reauthenticateMenuItem.show && (
            <Menu.Item
              key={reauthenticateMenuItem.label}
              onClick={async () => {
                await reauthenticateMenuItem.action();
                void sendEvent('hr_system_sync_menu_click', {
                  employerId,
                  action_category: 'reauthenticate',
                  action_name: reauthenticateMenuItem.label,
                });
              }}
            >
              {reauthenticateMenuItem.label}
            </Menu.Item>
          )}
          {disconnectMenuItem.show && (
            <Menu.Item
              key={disconnectMenuItem.label}
              onClick={() => {
                disconnectMenuItem.action();
                void sendEvent('hr_system_sync_menu_click', {
                  employerId,
                  action_category: 'disconnect',
                  action_name: disconnectMenuItem.label,
                });
              }}
            >
              {disconnectMenuItem.label}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      <Drawer
        onClose={() => setIsSetupOpen(false)}
        opened={isSetupOpen}
        title="Payroll sync settings"
        size="md"
      >
        <FinchSyncSettingsForm
          onClose={() => setIsSetupOpen(false)}
          employerId={employerId}
        />
      </Drawer>
      <Drawer
        onClose={handleCloseEligibilityDrawerAndPopUpModal}
        opened={isEligibilityOpen}
        title="Eligibility logic and field mapping configuration"
        size="lg"
      >
        <FinchEligibilityForm
          onCloseEligibilityDrawerAndPopUpModal={
            handleCloseEligibilityDrawerAndPopUpModal
          }
          onCloseAfterFormSubmit={closeAndResetEligibility}
          finchEmployerId={finchEmployerId}
          employerId={employerId}
          classDefinitions={classDefinitions}
          isFetchingClassDefinitions={isFetchingClassDefinitions}
          onDirtyChange={setIsEligibilityDirty}
        />
      </Drawer>
      <ModalMessage
        isOpen={isDisconnectPayrollModalOpen}
        onClose={() => setIsDisconnectPayrollModalOpen(false)}
        variant="negative"
        title="Disconnect HR system sync"
        description={`Are you sure you want to disconnect ${finchProviderDisplayName} from Zorro?`}
        primaryLabel="Disconnect"
        onPrimaryAction={() => disconnect([{ employerId }])}
        secondaryLabel="Cancel"
        onSecondaryAction={() => setIsDisconnectPayrollModalOpen(false)}
      />

      <ModalMessage
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        variant="regular"
        title="Discard changes?"
        description="You've made changes to this form. If you leave this page, your changes won't be saved."
        primaryLabel="Keep editing"
        onPrimaryAction={() => {
          setIsDiscardModalOpen(false);
          void sendEvent(
            'click_keep_editing_eligibility_form_discard_changes_modal',
            {
              employerId,
            }
          );
        }}
        secondaryLabel="Discard changes"
        onSecondaryAction={handleDiscardChanges}
      />
    </>
  );
};
